import { useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { ArrowRight, ChevronDown } from "lucide-react";
import SocialIcon from "@/components/SocialIcon";
import { PRODUCTS, SOCIALS } from "@/data/content";

export default function Hero() {
  const root = useRef(null);

  useLayoutEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
      tl.from("[data-hero-word]", {
        yPercent: 110,
        opacity: 0,
        duration: 1.1,
        stagger: 0.08,
        delay: 0.15,
      })
        .from("[data-hero-sub]", { y: 26, opacity: 0, duration: 0.9 }, "-=0.55")
        .from("[data-hero-cta]", { y: 20, opacity: 0, duration: 0.7, stagger: 0.12 }, "-=0.5")
        .from(
          "[data-hero-img]",
          { clipPath: "inset(12% 12% 12% 12% round 2rem)", scale: 1.12, opacity: 0, duration: 1.3, ease: "power2.out" },
          "-=1.1"
        )
        .from("[data-hero-chip]", { y: 34, opacity: 0, duration: 0.8, stagger: 0.14 }, "-=0.7");

      gsap.to("[data-hero-chip]", {
        y: -10,
        duration: 2.6,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
        stagger: { each: 0.4 },
      });

      // Failsafe: never leave hero content hidden if animations get interrupted
      // (e.g. iOS in-app browsers throttling rAF, mid-deploy asset reloads)
      gsap.delayedCall(4, () => {
        gsap.set("[data-hero-word], [data-hero-sub], [data-hero-cta], [data-hero-img], [data-hero-chip]", {
          clearProps: "all",
        });
      });

      // Gentle cursor parallax on the visual column
      const section = root.current;
      const chips = section.querySelectorAll("[data-hero-chip]");
      const img = section.querySelector("[data-hero-img]");
      const onMove = (e) => {
        const r = section.getBoundingClientRect();
        const dx = (e.clientX - r.left) / r.width - 0.5;
        const dy = (e.clientY - r.top) / r.height - 0.5;
        gsap.to(chips, { x: dx * 22, duration: 0.9, ease: "power2.out", overwrite: "auto" });
        gsap.to(img, { x: dx * 12, y: dy * 10, duration: 1.1, ease: "power2.out", overwrite: "auto" });
      };
      if (window.matchMedia("(pointer: fine)").matches) {
        section.addEventListener("mousemove", onMove);
      }
      return () => section.removeEventListener("mousemove", onMove);
    }, root);
    return () => ctx.revert();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={root}
      data-testid="hero-section"
      className="relative overflow-hidden water-gradient grain pt-28 md:pt-36 pb-16 md:pb-24"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          {/* Text column */}
          <div className="lg:col-span-7 relative z-10">
            <p data-hero-sub className="eyebrow text-water-deep/80">
              JINSU-suihkusuodattimet
            </p>
            <h1
              data-testid="hero-headline"
              className="h-display font-heading mt-6 font-semibold text-foreground"
              lang="fi"
            >
              {["Puhtaampaa", "vettä", "ihollesi", "ja", "hiuksillesi."].map((w, i) => (
                <span key={i} className="inline-block overflow-hidden pb-1 -mb-1 align-bottom">
                  <span data-hero-word className="inline-block">
                    {w}
                    {i < 4 ? " " : ""}
                  </span>
                </span>
              ))}
            </h1>
            <p data-hero-sub className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-muted-foreground">
              JINSU puhdistaa suihkuveden juuri ennen käyttöä – poistaa ruosteen, kloorin ja
              hienojakoiset epäpuhtaudet, jotka voivat kuivattaa ihoa ja hiuksia. Asennus käden
              käänteessä, ilman työkaluja.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/kauppa/"
                data-hero-cta
                data-testid="hero-cta-shop"
                className="inline-flex items-center gap-2.5 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-primary-foreground shadow-lg shadow-primary/25 transition-transform duration-200 hover:scale-[1.04]"
              >
                Tutustu tuotteisiin <ArrowRight size={17} />
              </Link>
              <button
                onClick={() => scrollTo("suodatus")}
                data-hero-cta
                data-testid="hero-cta-how"
                className="inline-flex items-center gap-2.5 rounded-full border border-primary/25 bg-white/60 px-7 py-3.5 text-sm font-bold text-primary backdrop-blur-sm transition-colors duration-200 hover:bg-white"
              >
                Näin suodatin toimii <ChevronDown size={17} />
              </button>
            </div>
            <p data-hero-sub className="mt-8 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Toimitus Postilla 2–5 arkipäivässä · Koko Suomi
            </p>
            <div data-hero-cta className="mt-6 flex items-center gap-3" data-testid="hero-socials">
              {SOCIALS.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${s.name}: ${s.label}`}
                  data-testid={`hero-social-${s.name.toLowerCase()}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-primary/20 bg-white/60 text-water-deep backdrop-blur-sm transition-all duration-200 hover:scale-110 hover:bg-white"
                >
                  <SocialIcon name={s.name} size={17} strokeWidth={1.8} />
                </a>
              ))}
            </div>
          </div>

          {/* Visual column */}
          <div className="lg:col-span-5 relative">
            <div data-hero-img className="relative aspect-[4/5] overflow-hidden rounded-[2rem] rounded-t-[8rem] bg-water-soft shadow-2xl shadow-primary/20">
              <img
                src="/images/hero-main.jpg"
                alt="JINSU-suihkusuodatin – puhtaampaa vettä iholle ja hiuksille"
                className="w-full aspect-[4/5] object-cover"
                fetchPriority="high"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/25 via-transparent to-transparent" />
            </div>

            {/* Floating product chips */}
            {PRODUCTS.slice(0, 3).map((p, i) => (
              <Link
                key={p.slug}
                to={`/tuote/${p.slug}/`}
                data-hero-chip
                data-testid={`hero-chip-${p.slug}`}
                className={`glass absolute flex items-center gap-3 rounded-2xl p-3 pr-5 shadow-xl shadow-primary/10 transition-transform duration-200 hover:scale-105 ${
                  i === 0
                    ? "-left-4 top-[12%] md:-left-10"
                    : i === 1
                      ? "-right-3 top-[42%] md:-right-8"
                      : "-left-2 bottom-[8%] md:-left-6"
                }`}
              >
                <img
                  src={p.image}
                  alt=""
                  className="h-12 w-12 rounded-xl object-cover"
                  loading="lazy"
                />
                <span>
                  <span className="block text-[11px] font-bold leading-tight text-foreground">
                    {p.name.replace("Jinsu ", "")}
                  </span>
                  <span className="block text-[11px] font-extrabold text-water-deep">{p.priceLabel}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
